import { useMemo, useState } from 'react'

import type { BiddingBlockApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import type { BiddingSpotApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'

import { useBulkWithdrawBidding } from '~/pages/SpotBidding/hooks/useBulkWithdrawBidding'
import type { HoldForBulkWithdraw } from '~/pages/SpotBidding/hooks/useSpotWithdrawResultNotices'
import type { BlockBidRow } from '~/pages/SpotBidding/models/blockBidRow'
import type { BulkWithdrawOutcome } from '~/pages/SpotBidding/models/bulkWithdrawResult'
import {
  type BulkWithdrawMenuState,
  type BulkWithdrawRequest,
  buildBulkWithdrawRequest,
  resolveBlockBulkWithdrawTargetIds,
  resolveBulkWithdrawMenuState,
  resolveSpotBulkWithdrawTargetIds,
} from '~/pages/SpotBidding/models/bulkWithdrawTargets'
import type { PageListScope } from '~/pages/SpotBidding/models/pageIssuerScope'
import type { SharedBidListFilter } from '~/pages/SpotBidding/models/sharedBidListFilter'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'

/** ⋯ メニュー項目「入札中の札を一括で取下」 に渡す状態と、 押した時の操作。 */
export type BulkWithdrawMenu = {
  state: BulkWithdrawMenuState
  /** 押せる時のツールチップに出す、 通常入札の札の数とブロック入札のグループ数。 */
  normalCount: number
  blockCount: number
  onOpen: () => void
}

export type BulkWithdrawControl = {
  menu: BulkWithdrawMenu
  /** 直近の実行結果 (受け付けなかった札・結果を確認できなかった札がある時だけ)。 */
  outcome: BulkWithdrawOutcome | undefined
  dismissOutcome: () => void
  /** 結果のバナーの「再読み込み」。 */
  reloadLists: () => void
  dialog: {
    isOpen: boolean
    spotCount: number
    blockCount: number
    isSubmitting: boolean
    onClose: () => void
    onConfirm: () => void
  }
}

type Params = {
  /** 受渡日の全札 (絞り込み前)。 */
  bids: readonly SpotBidRow[]
  /** 受渡日の全ブロック入札の Leg 行 (絞り込み前)。 */
  blockBidRows: readonly BlockBidRow[]
  /** 楽観ロックの基準値 (申請セット・グループの updatedAt) を引く元。 */
  spotRequests: readonly BiddingSpotApprovalRequest[]
  blockRequests: readonly BiddingBlockApprovalRequest[]
  /**
   * 一覧応答の、 一括取下が作ったワークフローを理由に受け付けない workflowId の集合 (AC-BSP-058)。
   * 通常入札は確定済のワークフロー、 ブロック入札は確定済か記録したワークフローのケースが見つからない
   * もの (backend の一括取下の判定と同じ)。 札の種類ごとに別の集合を使う。
   */
  spotExcludedWorkflowIds: ReadonlySet<string>
  blockExcludedWorkflowIds: ReadonlySet<string>
  /** 共通ツールバーの範囲 (エリア・発注主体区分)。 両タブの一覧と同じ値。 */
  scope: PageListScope
  /** 両タブで共有する一覧の絞り込み (売/買・ステータス。 AC-BSP-019)。 */
  listFilter: SharedBidListFilter
  isBeforeDeadline: boolean
  /** 確定済ワークフロー経由で画面が読み取り専用か。 */
  isReadonly: boolean
  /**
   * 通常入札 / ブロック入札の明細や一覧を取得できていない間 (取得中・取得失敗・未反映の差分)。
   * 対象の件数が確定しないので、 どちらかが true の間は項目を選べなくする。
   */
  isSpotUnresolved: boolean
  isBlockUnresolved: boolean
  /** 結果を受渡日ごとに切り替える。 */
  targetDate: Date
  /** 一括取下で送る札の結果を件数のトーストで知らせる (AC-BSP-116。 `useSpotWithdrawResultNotices`)。 */
  holdNotices: HoldForBulkWithdraw
}

/**
 * 入札中の札の一括取下 (AC-BSP-116) の、 page 側の状態。
 *
 * 対象は page が持つ共通ツールバーの範囲と両タブで共有する絞り込み (AC-BSP-019) から数えるので、
 * 通常入札タブとブロック入札タブのどちらの ⋯ メニューから押しても、 タブを開いたかどうかによらず
 * 同じ件数になる。
 *
 * 確認ダイアログを開いた時点で対象と楽観ロックの基準値を確定し、 件数と送る内容を同じにする。
 * 開いた後に他の操作で更新された申請セットの札は、 backend が拒否する (AC-BSP-124)。
 */
export const useBulkWithdrawControl = ({
  bids,
  blockBidRows,
  spotRequests,
  blockRequests,
  spotExcludedWorkflowIds,
  blockExcludedWorkflowIds,
  scope,
  listFilter,
  isBeforeDeadline,
  isReadonly,
  isSpotUnresolved,
  isBlockUnresolved,
  targetDate,
  holdNotices,
}: Params): BulkWithdrawControl => {
  const { isRunning, withdraw, outcome, dismissOutcome, reloadLists } =
    useBulkWithdrawBidding(targetDate, holdNotices)

  const spotIds = useMemo(
    () =>
      resolveSpotBulkWithdrawTargetIds(bids, {
        scope,
        listFilter,
        isBeforeDeadline,
        isWorkflowReadonly: isReadonly,
        excludedWorkflowIds: spotExcludedWorkflowIds,
      }),
    [
      bids,
      scope,
      listFilter,
      isBeforeDeadline,
      isReadonly,
      spotExcludedWorkflowIds,
    ],
  )
  const blockIds = useMemo(
    () =>
      resolveBlockBulkWithdrawTargetIds(blockBidRows, {
        scope,
        listFilter,
        isBeforeDeadline,
        isWorkflowReadonly: isReadonly || isBlockUnresolved,
        excludedWorkflowIds: blockExcludedWorkflowIds,
      }),
    [
      blockBidRows,
      scope,
      listFilter,
      isBeforeDeadline,
      isReadonly,
      isBlockUnresolved,
      blockExcludedWorkflowIds,
    ],
  )

  const [request, setRequest] = useState<BulkWithdrawRequest | undefined>(
    undefined,
  )
  const open = (): void => {
    const next = buildBulkWithdrawRequest({
      spotBidIds: spotIds,
      blockGroupIds: blockIds,
      bids,
      spotRequests,
      blockRequests,
    })
    if (next.spot.length + next.block.length === 0) return
    setRequest(next)
  }
  const confirm = async (): Promise<void> => {
    if (request === undefined) return
    await withdraw(request)
    setRequest(undefined)
  }

  return {
    menu: {
      state: resolveBulkWithdrawMenuState({
        isReadonly,
        isBeforeDeadline,
        isSpotUnresolved,
        isBlockUnresolved,
        targetCount: spotIds.length + blockIds.length,
      }),
      normalCount: spotIds.length,
      blockCount: blockIds.length,
      onOpen: open,
    },
    outcome,
    dismissOutcome,
    reloadLists,
    dialog: {
      isOpen: request !== undefined,
      spotCount: request?.spot.length ?? 0,
      blockCount: request?.block.length ?? 0,
      isSubmitting: isRunning,
      onClose: () => setRequest(undefined),
      onConfirm: () => {
        void confirm()
      },
    },
  }
}
